import { useState, useEffect } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { Service, ServiceVisibility, ServiceCategory } from '../lib/db/types';
import { serviceConverter, serviceCategoryConverter } from '../lib/db/converters';

interface ServiceWithId extends Service {
  id: string;
}

interface ServiceCategoryWithId extends ServiceCategory {
  id: string;
}

type VisibilityFilter = 'all' | 'public' | 'private';
type StatusFilter = 'all' | 'active' | 'inactive';

export function ServicesPage() {
  const { tenantId, role } = useTenant();
  const navigate = useNavigate();

  const [services, setServices] = useState<ServiceWithId[]>([]);
  const [filteredServices, setFilteredServices] = useState<ServiceWithId[]>([]);
  const [categories, setCategories] = useState<ServiceCategoryWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const canEdit = role === 'owner' || role === 'manager';

  // Load services and categories
  useEffect(() => {
    if (!tenantId) return;

    const loadData = async () => {
      try {
        setLoading(true);

        // Load categories
        const categoriesQuery = query(
          collection(db, 'serviceCategories').withConverter(serviceCategoryConverter),
          where('tenantId', '==', tenantId),
          where('isActive', '==', true),
          orderBy('displayOrder')
        );
        const categoriesSnap = await getDocs(categoriesQuery);
        const categoriesData = categoriesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ServiceCategoryWithId[];
        setCategories(categoriesData);

        // Load services
        const servicesQuery = query(
          collection(db, 'services').withConverter(serviceConverter),
          where('tenantId', '==', tenantId),
          orderBy('displayOrder')
        );
        const servicesSnap = await getDocs(servicesQuery);
        const servicesData = servicesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ServiceWithId[];

        setServices(servicesData);
        setFilteredServices(servicesData);
      } catch (err: any) {
        console.error('Error loading services:', err);
        setError('Failed to load services');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId]);

  // Apply filters
  useEffect(() => {
    let filtered = services;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          s.description?.toLowerCase().includes(term)
      );
    }

    // Visibility filter
    if (visibilityFilter !== 'all') {
      filtered = filtered.filter((s) => s.visibility === visibilityFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((s) =>
        statusFilter === 'active' ? s.isActive : !s.isActive
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((s) => s.categoryId === categoryFilter);
    }

    setFilteredServices(filtered);
  }, [searchTerm, visibilityFilter, statusFilter, categoryFilter, services]);

  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return '-';
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || categoryId;
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return '-';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const handleDeleteService = async (serviceId: string, e: MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this service?')) return;

    try {
      await deleteDoc(doc(db, 'services', serviceId));
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
      setSuccess('Service deleted successfully');
    } catch (err: any) {
      console.error('Error deleting service:', err);
      setError('Failed to delete service');
    }
  };

  const handleEditService = (serviceId: string, e: MouseEvent) => {
    e.stopPropagation();
    navigate(`/services/${serviceId}/edit`);
  };

  const getVisibilityColor = (visibility: ServiceVisibility): 'success' | 'default' => {
    return visibility === 'public' ? 'success' : 'default';
  };

  const isProviderlessService = (service: ServiceWithId) =>
    service.bookingMode === 'class_session';
  const needsProvider = (service: ServiceWithId) =>
    service.isActive && !isProviderlessService(service) && (service.providerIds?.length || 0) === 0;
  const bookingModeLabel = (service: ServiceWithId) => {
    if (service.bookingMode === 'class_session') return 'Class';
    return 'Appointment';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1">
          Services
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/services/new')}
          >
            Create Service
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search services..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200, flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Visibility"
          value={visibilityFilter}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVisibilityFilter(e.target.value as VisibilityFilter)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="public">Public</MenuItem>
          <MenuItem value="private">Private</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setStatusFilter(e.target.value as StatusFilter)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Services Grid */}
      {filteredServices.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              {services.length === 0
                ? 'No services yet. Create your first service to get started.'
                : 'No services match your filters.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredServices.map((service) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={service.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.2s',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => navigate(`/services/${service.id}/edit`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header with color indicator */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: service.color || '#1976d2',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
                      {service.name}
                    </Typography>
                  </Box>

                  {/* Description */}
                  {service.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} noWrap>
                      {service.description}
                    </Typography>
                  )}

                  {/* Details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Duration:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDuration(service.duration)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Price:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatPrice(service.price, service.currency)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Category:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {getCategoryName(service.categoryId)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Chips */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {needsProvider(service) ? (
                      <Chip
                        label="Needs provider"
                        size="small"
                        color="warning"
                      />
                    ) : (
                      <>
                        <Chip
                          label={bookingModeLabel(service)}
                          size="small"
                          color={isProviderlessService(service) ? 'primary' : 'default'}
                        />
                        <Chip
                          label={service.visibility}
                          size="small"
                          color={getVisibilityColor(service.visibility)}
                        />
                        <Chip
                          label={service.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={service.isActive ? 'success' : 'default'}
                        />
                      </>
                    )}
                  </Box>
                </CardContent>

                {/* Actions */}
                {canEdit && (
                  <Box
                    sx={{
                      p: 1,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e: MouseEvent<HTMLElement>) => handleEditService(service.id, e)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e: MouseEvent<HTMLElement>) => handleDeleteService(service.id, e)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
